import { lazy } from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, Route, useLocation } from "react-router-dom";
import ModuleRouteGuard from "../common/components/ModuleRouteGuard";
import { isMedicalDirectorUser } from "../common/utils/breakGlass";
import type { RootState } from "../redux/store/store";
import { PatientFacesheetRedirect } from "./redirects";
import MainLayout from "./layouts/main-layout";
import PrivateRoute from "./private-route";

const InvoiceLayout = lazy(() => import("../common/components/InvoiceLayout"));
const EHRMainPage = lazy(() => import("../modules/EHR"));
const CarePlan = lazy(() => import("../modules/EHR/CarePlan"));
const CPOE = lazy(() => import("../modules/EHR/CPOE"));
const CPOEMessageWidget = lazy(() => import("../modules/EHR/CPOE/Message"));
const LabResults = lazy(() => import("../modules/EHR/CPOE/LabResults"));
const Order = lazy(() => import("../modules/EHR/Order"));
const PatientListing = lazy(() => import("../modules/EHR/Patients"));
const CrossClinicPatients = lazy(
  () => import("../modules/EHR/CrossClinicPatients"),
);
const PatientCharting = lazy(() => import("../modules/EHR/Patients/Charting"));
const AllergiesAndIntolerance = lazy(
  () => import("../modules/EHR/Patients/Charting/AllergiesAndIntolerance"),
);
const Notes = lazy(() => import("../modules/EHR/Patients/Charting/Notes"));
const Reports = lazy(() => import("../modules/EHR/Patients/Charting/Reports"));
const MembershipAndProfile = lazy(
  () => import("../modules/EHR/Patients/Charting/MembershipAndProfile"),
);
const Questionnaires = lazy(
  () => import("../modules/EHR/Patients/Charting/dashboard/Questionnaries"),
);
const PatientChartingDashboard = lazy(
  () => import("../modules/EHR/Patients/Charting/PatientChartingDashboard"),
);
const Diagnosis = lazy(
  () => import("../modules/EHR/Patients/Charting/Diagnosis"),
);
const Documents = lazy(
  () => import("../modules/EHR/Patients/Charting/Documents"),
);
const DrugIntolerance = lazy(
  () => import("../modules/EHR/Patients/Charting/Drug-Intolerance"),
);
const History = lazy(() => import("../modules/EHR/Patients/Charting/History"));
const Imaging = lazy(() => import("../modules/EHR/Patients/Charting/Imaging"));
const Invoice = lazy(() => import("../modules/EHR/Patients/Charting/Invoice"));
const Labs = lazy(() => import("../modules/EHR/Patients/Charting/Labs"));
const Message = lazy(
  () => import("../modules/EHR/Patients/Charting/Messaging/src/App"),
);
const Pharmacies = lazy(
  () => import("../modules/EHR/Patients/Charting/Pharmacies"),
);
const Profile = lazy(() => import("../modules/EHR/Patients/Charting/Profile"));
const PatientTasks = lazy(
  () => import("../modules/EHR/Patients/Charting/Tasks"),
);
const Timeline = lazy(() =>
  import("../modules/EHR/Patients/Charting/Timeline").then((m) => ({
    default: m.Timeline,
  })),
);
const Vaccines = lazy(
  () => import("../modules/EHR/Patients/Charting/Vaccines"),
);
const VisitDetails = lazy(
  () => import("../modules/EHR/Patients/Charting/visit-details"),
);
const Vitals = lazy(() => import("../modules/EHR/Patients/Charting/Vitals"));
const SignupWithPHP = lazy(() => import("../modules/EHR/Patients/sign-up"));
const ReferralOut = lazy(() => import("../modules/EHR/Referral"));
const EducationMaterial = lazy(
  () => import("../modules/EHR/EducationMaterial"),
);
const Scheduling = lazy(() => import("../modules/EHR/scheduling"));
const EncounterSummary = lazy(
  () => import("../modules/EHR/scheduling/EncounterSummary"),
);
const VisitNote = lazy(() => import("../modules/EHR/scheduling/visit-notes"));
const Settings = lazy(() => import("../modules/EHR/Settings"));
const AppointmentTypeScreen = lazy(
  () => import("../modules/EHR/Settings/AppintmentType"),
);
const AvailabilityView = lazy(
  () => import("../modules/EHR/Settings/Availability"),
);
const CalenderSync = lazy(() => import("../modules/EHR/Settings/CalenderSync"));
const ConfigureAvailabilityForm = lazy(
  () => import("../modules/EHR/Settings/Availability/forms/configuration"),
);
const CustomizableInformationSharing = lazy(
  () => import("../modules/EHR/Settings/CustomizableInformationSharing"),
);
const ProfileSettings = lazy(() => import("../modules/EHR/Settings/Profile"));
const StatusConfiguration = lazy(
  () => import("../modules/EHR/Settings/StatusConfiguration"),
);
const Tasks = lazy(() => import("../modules/EHR/Tasks"));
const AllTasks = lazy(() => import("../modules/EHR/Tasks/AllTasks"));
const GroupTask = lazy(() => import("../modules/EHR/Tasks/GroupTasks"));
const IndividualTask = lazy(
  () => import("../modules/EHR/Tasks/IndividualTask"),
);
const RemindersTask = lazy(() => import("../modules/EHR/Tasks/ReminderTask"));
const ResolvedTask = lazy(() => import("../modules/EHR/Tasks/ResolvedTasks"));
const SelfTask = lazy(() => import("../modules/EHR/Tasks/SelfTask"));
const Templates = lazy(() => import("../modules/EHR/Templates"));
const AnnotableImage = lazy(
  () => import("../modules/EHR/Templates/AnnotableImage"),
);
const Macros = lazy(() => import("../modules/EHR/Templates/Macros"));
const PhysicalExam = lazy(
  () => import("../modules/EHR/Templates/PhysicalExam"),
);
const NewPE = lazy(() => import("../modules/EHR/Templates/PhysicalExam/NewPE"));
const ReviewOfSystem = lazy(() => import("../modules/EHR/Templates/ROS"));
const NewROS = lazy(() => import("../modules/EHR/Templates/ROS/NewROS"));
const VisitNotes = lazy(() => import("../modules/EHR/Templates/VisitNotes"));
const NewVisitNote = lazy(
  () => import("../modules/EHR/Templates/VisitNotes/NewVisitNote"),
);
const ViewEditNote = lazy(
  () => import("../modules/EHR/Templates/VisitNotes/ViewEditNote"),
);
const VisitNoteView = lazy(
  () => import("../modules/EHR/Patients/Charting/VisitNote/View"),
);
const FamilyHistoryView = lazy(
  () => import("../modules/EHR/Patients/Charting/History/Tabs/Family/View"),
);
const AddVisitNote = lazy(
  () => import("../modules/EHR/Patients/Charting/VisitNote/Form"),
);
const Fax = lazy(() => import("../modules/EHR/Fax"));
const PatientsEducationMaterial = lazy(
  () => import("../modules/EHR/Patients/Charting/education-material"),
);
const LabResultDashboard = lazy(
  () => import("../modules/EHR/Dashboard/Lab-Result"),
);
const DashBoardTasks = lazy(() => import("../modules/EHR/Dashboard/Tasks"));
const ERxRequestsDashboard = lazy(
  () => import("../modules/EHR/Dashboard/ERxRequests"),
);
const UnassignedEncounters = lazy(
  () => import("../modules/EHR/Dashboard/Unsigned Enconters"),
);
const RosInfo = lazy(
  () => import("../modules/EHR/Templates/ROS/Detail/RosInfo"),
);
const PhysicalExamInfo = lazy(
  () => import("../modules/EHR/Templates/PhysicalExam/Detail/PhysicalExamInfo"),
);
const POCTests = lazy(() => import("../modules/EHR/Settings/POCTests"));
const AllPOC = lazy(() => import("../modules/EHR/Settings/POCTests/AllPOC"));
const AddPOC = lazy(() => import("../modules/EHR/Settings/POCTests/AddPOC"));
const DocumentTypes = lazy(
  () => import("../modules/EHR/Settings/DocumentTypes"),
);
const ProviderDirectory = lazy(
  () => import("../modules/EHR/Settings/ProviderDirectory"),
);
const NurseRequests = lazy(
  () => import("../modules/EHR/Settings/NurseRequests"),
);
const VideoCallRoute = lazy(() => import("../modules/EHR/VideoCall"));

const MainRoute = () => (
  <PrivateRoute>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </PrivateRoute>
);

const SettingsPatientsRedirect = () => {
  const location = useLocation();
  const targetPath = location.pathname.replace("/ehr/settings", "/ehr");

  return (
    <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />
  );
};

// The Settings patient list is for admins and medical directors; every other
// role's list is the Patients tab.
const SettingsPatientListing = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  if (user?.roleType !== "ADMIN_USER" && !isMedicalDirectorUser(user)) {
    return <Navigate to="/ehr/cross-clinic-patients" replace />;
  }
  return (
    <ModuleRouteGuard
      moduleType="ehr"
      requiredPermissions={["EHR Patients"]}
      redirectTo="/ehr/cross-clinic-patients"
    >
      <PatientListing />
    </ModuleRouteGuard>
  );
};

const EhrRoutes = (
  <Route path="/ehr" element={<MainRoute />}>
    <Route path="video-call/:roomId" element={<VideoCallRoute />} />
    <Route
      path="dashboard"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="dashboard">
          <EHRMainPage />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="dashboard/tasks"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="dashboard">
          <DashBoardTasks />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="dashboard/lab-results"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="dashboard">
          <LabResultDashboard />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="dashboard/erx-requests"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="dashboard">
          <ERxRequestsDashboard />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="dashboard/unassigned-encounters"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="dashboard">
          <UnassignedEncounters />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="scheduling"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="scheduling">
          <Scheduling />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="scheduling/visit-note/:mode/:id"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="scheduling">
          <VisitNote />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="patients/visit-note/add/:patientId"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="scheduling">
          <AddVisitNote mode={"add"} />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="patients/visit-note/update/:patientId/:id"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="scheduling">
          <AddVisitNote mode={"update"} />
        </ModuleRouteGuard>
      }
    />
    {/* Old patient list paths; the list is now the Patients tab. The static
        paths outrank `patients/:id` below. */}
    {["patients", "patients/retail", "patients/employee"].map((path) => (
      <Route
        key={path}
        path={path}
        element={<Navigate to="/ehr/cross-clinic-patients" replace />}
      />
    ))}
    <Route
      path="patients/sign-up"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="patients">
          <SignupWithPHP />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="patients/:id"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="patients">
          <PatientCharting />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<PatientFacesheetRedirect />} />
      <Route path="dashboard" element={<PatientFacesheetRedirect />} />
      <Route path="dashboard/*" element={<PatientFacesheetRedirect />} />
      <Route
        path="charting-dashboard"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-dashboard">
            <PatientChartingDashboard />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="facesheet"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-dashboard">
              <PatientChartingDashboard />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="notes"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-dashboard">
              <PatientChartingDashboard />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="medication"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-dashboard">
              <PatientChartingDashboard />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="orders"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-dashboard">
              <PatientChartingDashboard />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="referrals"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-dashboard">
              <PatientChartingDashboard />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="letters"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-dashboard">
              <PatientChartingDashboard />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="timeline"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-timeline">
            <Timeline />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="questionnaires"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-dashboard">
            <Questionnaires />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="reports/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
            <Reports />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="documents"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Reports />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="lab-results"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Reports />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="poc-results"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Reports />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="imaging-reports"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Reports />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="questionnaires"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Reports />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="clinical-notes/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
            <Notes />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="visit-notes"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Notes />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="nurse-notes"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Notes />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="interaction-notes"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Notes />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="phone-non-visit-notes"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Notes />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="allergies/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-allergies">
            <AllergiesAndIntolerance />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="allergy"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-allergies">
              <AllergiesAndIntolerance />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="drug-intolerance"
          element={
            <ModuleRouteGuard
              moduleType="ehr"
              routePath="patient-drug-tolerance"
            >
              <AllergiesAndIntolerance />
            </ModuleRouteGuard>
          }
        />
      </Route>
      {/* Legacy standalone route, kept so existing deep links stay valid. */}
      <Route
        path="drug-intolerance"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-drug-tolerance">
            <DrugIntolerance />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="diagnosis/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-diagnosis">
            <Diagnosis />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="active"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-diagnosis">
              <Diagnosis />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="resolved"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-diagnosis">
              <Diagnosis />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="vaccine/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-vaccines">
            <Vaccines />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="vaccines"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-vaccines">
              <Vaccines />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="immunization"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-vaccines">
              <Vaccines />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="visit-details/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-visit-details">
            <VisitDetails />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="upcoming"
          element={
            <ModuleRouteGuard
              moduleType="ehr"
              routePath="patient-visit-details"
            >
              <VisitDetails />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="past"
          element={
            <ModuleRouteGuard
              moduleType="ehr"
              routePath="patient-visit-details"
            >
              <VisitDetails />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="visit-note-view/:type/:noteId"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-visit-details">
            <VisitNoteView />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="family-history-view/:familyId"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-history">
            <FamilyHistoryView />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="imaging/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-imaging">
            <Imaging />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="order"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-imaging">
              <Imaging />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="results"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-imaging">
              <Imaging />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="labs/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-labs">
            <Labs />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="labResults"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-labs">
              <Labs />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="labOrders"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-labs">
              <Labs />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="pocResults"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-labs">
              <Labs />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="pharmacies"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-pharmacies">
            <Pharmacies />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="documents/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
            <Documents />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="docs"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Documents />
            </ModuleRouteGuard>
          }
        >
          {/* <Route
            path=":docType"
            element={
              <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
                <Documents />
              </ModuleRouteGuard>
            }
          /> */}
        </Route>
        {/* Interaction Notes moved to the Notes sidebar item. Kept so stale
            /documents/notes links still land on Reports rather than falling
            through to the app-wide role redirect. */}
        <Route
          path="notes"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
              <Documents />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="documents"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-documents">
            <Documents />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="messages"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-messages">
            <Message />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="education"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patients">
            <PatientsEducationMaterial />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="patienttasks"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-tasks">
            <PatientTasks />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="all"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-tasks">
              <AllTasks />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="self"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-tasks">
              <SelfTask />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="groups"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-tasks">
              <GroupTask />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="individual"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-tasks">
              <IndividualTask />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="reminders"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-tasks">
              <RemindersTask />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="resolved"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-tasks">
              <ResolvedTask />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="history/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-history">
            <History />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="pastMedicalHistory"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-history">
              <History />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="pastSurgicalHistory"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-history">
              <History />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="familyHistory"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-history">
              <History />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="socialHistory"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-history">
              <History />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="habits"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-history">
              <History />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="screeningHistory"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-history">
              <History />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="invoice"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-invoice">
            <Invoice />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="invoice/:invoiceUuid/:mode?"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-invoice">
            <InvoiceLayout />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="bills/:invoiceUuid/:mode?"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-invoice">
            <InvoiceLayout />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="membership/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-membership">
            <MembershipAndProfile />
          </ModuleRouteGuard>
        }
      >
        <Route
          path="details"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-membership">
              <MembershipAndProfile />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="profile"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-membership">
              <MembershipAndProfile />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="portal-access"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-membership">
              <MembershipAndProfile />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="portal-access-audit-logs"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-membership">
              <MembershipAndProfile />
            </ModuleRouteGuard>
          }
        />

        <Route
          path="patient-communications"
          element={
            <ModuleRouteGuard moduleType="ehr" routePath="patient-membership">
              <MembershipAndProfile />
            </ModuleRouteGuard>
          }
        />
      </Route>
      <Route
        path="vitals"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-tasks">
            <Vitals />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="profile/"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="patient-profile">
            <Profile />
          </ModuleRouteGuard>
        }
      />
    </Route>
    <Route
      path="scheduling/encounter-summary/:id"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="scheduling">
          <EncounterSummary />
        </ModuleRouteGuard>
      }
    />
    {/* <Route path="scheduling/list/" element={<AppointmentListView />}>
      <Route path="appointments-list" element={<AppointmentListView />} />
      <Route path="unsigned-visits" element={<AppointmentListView />} />
      <Route path="signed-visits" element={<AppointmentListView />} />
    </Route> */}

    <Route
      path="orders"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="cpoe">
          <Order />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="rx" replace />} />
      <Route
        path="rx"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="cpoe">
            <CPOEMessageWidget />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="cpoe"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="cpoe">
            <LabResults />
          </ModuleRouteGuard>
        }
      />
    </Route>
    <Route
      path="rx"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="cpoe">
          <CPOE />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="messages" replace />} />
      <Route
        path="messages"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="cpoe">
            <CPOEMessageWidget />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="lab-results"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="cpoe">
            <LabResults />
          </ModuleRouteGuard>
        }
      />
    </Route>
    <Route
      path="cpoe"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="cpoe">
          <CPOE />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="lab-results" replace />} />
      <Route
        path="lab-results"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="cpoe">
            <LabResults />
          </ModuleRouteGuard>
        }
      />
    </Route>
    <Route
      path="settings"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="settings">
          <Settings />
        </ModuleRouteGuard>
      }
    >
      <Route
        path="availability"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="availability">
            <AvailabilityView />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="calender-sync"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="calender-sync">
            <CalenderSync />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="configure"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="availability">
            <ConfigureAvailabilityForm />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="appointment-types"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="appointment-type">
            <AppointmentTypeScreen />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="status-configuration"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="status-configuration">
            <StatusConfiguration />
          </ModuleRouteGuard>
        }
      />
      {/* Deeper links (old visit note URLs) keep their path under /ehr. */}
      {["patients", "patients/retail", "patients/employee"].map((path) => (
        <Route key={path} path={path} element={<SettingsPatientListing />} />
      ))}
      {/* There is no employer tab; old links land on the employee one. */}
      <Route
        path="patients/employer"
        element={<Navigate to="/ehr/settings/patients/employee" replace />}
      />

      <Route path="patients/*" element={<SettingsPatientsRedirect />} />
      <Route
        path="care-plans"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="care-plans">
            <CarePlan />
          </ModuleRouteGuard>
        }
      />
      <Route path="education-material" element={<EducationMaterial />} />
      <Route path="templates" element={<Templates />}>
        <Route path="visit-notes" element={<VisitNotes />} />
        <Route path="physical-exam" element={<PhysicalExam />} />
        <Route path="review-of-system" element={<ReviewOfSystem />} />
        <Route path="macros" element={<Macros />} />
        <Route path="annotable-image" element={<AnnotableImage />} />
      </Route>
      <Route
        path="templates/visit-notes/new-visit-note"
        element={<NewVisitNote />}
      />
      <Route path="templates/review-of-system/:rosId" element={<RosInfo />} />
      <Route
        path="templates/physical-exam/:PEId"
        element={<PhysicalExamInfo />}
      />
      <Route
        path="templates/visit-note/:templateId"
        element={<ViewEditNote />}
      />
      <Route path="templates/updateros/:rosTempId" element={<NewROS />} />
      <Route path="templates/review-of-system/new-ros" element={<NewROS />} />
      <Route path="templates/physical-exam/new-pe" element={<NewPE />} />
      <Route
        path="templates/physical-exam/updatepe/:peTempId"
        element={<NewPE />}
      />
      <Route
        path="customizable-information-sharing"
        element={
          <ModuleRouteGuard
            moduleType="ehr"
            routePath="customizable-information-sharing"
          >
            <CustomizableInformationSharing />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="profile-settings"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="settings">
            <ProfileSettings />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="poc-test"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="settings">
            <POCTests />
          </ModuleRouteGuard>
        }
      >
        <Route index element={<Navigate to="all-poc" replace />} />
        <Route path="all-poc" element={<AllPOC />} />
        <Route path="add-poc" element={<AddPOC />} />
      </Route>
      <Route
        path="document-types"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="settings">
            <DocumentTypes />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="provider-directory"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="settings">
            <ProviderDirectory />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="nurse-requests"
        element={
          <ModuleRouteGuard moduleType="ehr" routePath="settings">
            <NurseRequests />
          </ModuleRouteGuard>
        }
      />
    </Route>

    <Route path="cross-clinic-patients" element={<CrossClinicPatients />} />

    <Route
      path="provider-directory"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="provider-directory">
          <ProviderDirectory />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="tasks/"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="tasks">
          <Tasks />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="all" replace />} />
      <Route path="all" element={<AllTasks />} />
      <Route path="self" element={<SelfTask />} />
      <Route path="groups" element={<GroupTask />} />
      <Route path="individual" element={<IndividualTask />} />
      <Route path="reminders" element={<RemindersTask />} />
      <Route path="resolved" element={<ResolvedTask />} />
    </Route>
    <Route
      path="referral"
      element={
        <ModuleRouteGuard moduleType="ehr" routePath="referral">
          <ReferralOut />
        </ModuleRouteGuard>
      }
    />
    <Route path="fax" element={<Fax />} />
  </Route>
);

export default EhrRoutes;
